// File: frontend/src/components/dfs/MomentReplayPanel.tsx
//
// The pinned top of the score breakdown modal: an animated pitch replay of
// the player's scoring moments (StatsBomb events, no video), a small minute +
// points badge, and the bar with Play all and the total.
//
// Owner-approved design (2026-09-29): opens on a still map (no autoplay); a
// row or a mark plays that moment; Play all runs every moment in match order
// with a running total. Opponents are unlabeled red dots; teammates are named
// while on the ball; the player's gold ring shows only while on the ball.
//
// The animation writes SVG attributes directly each frame (ReplayRenderer) so
// React never re-renders at 60 fps; React only hears about discrete changes.

import React, { forwardRef, useCallback, useEffect, useId, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Play, Square } from 'lucide-react';
import {
  ContextLine,
  Mark,
  MarkKind,
  QueueEntry,
  ReplayItem,
  ReplayMoment,
  Track,
  ballAt,
  featuredOnBall,
  BETWEEN_HOLD_SECONDS,
  END_HOLD_SECONDS,
  advanceTime,
  holderAt,
  labelOffset,
  labelSpots,
  labelWidth,
  markProgress,
  playerAt,
  queueForAll,
  queueForMoment,
  scoreStep,
  signedPoints,
  trailStart,
  visibleLines,
  visibleTracks,
} from '@/utils/momentReplay';

const COLORS = {
  pitchA: '#0e2240',
  pitchB: '#102647',
  pitchLine: 'rgba(232, 238, 248, 0.32)',
  us: '#e8eef8',
  them: '#d4566e',
  bg: '#0a1426',
  fg: '#e8eef8',
  muted: '#93a4c3',
  accent: '#ffc42c',
};

// Category colors, matching the row chips: shots green, creation blue,
// Impact yellow, lost ball red.
const MARK_COLORS: Record<MarkKind, string> = {
  shot: '#4ade80',
  create: '#78b4e6',
  'create-dot': '#78b4e6',
  impact: '#ffc42c',
  lost: '#f87171',
};

type PlaybackMode = 'still' | 'single' | 'all';

interface PlaybackState {
  mode: PlaybackMode;
  badgeKey: string | null;
  badgeHit: boolean;
  caption: string | null;
  poppedKeys: string[];
}

const STILL: PlaybackState = { mode: 'still', badgeKey: null, badgeHit: false, caption: null, poppedKeys: [] };

const SVGNS = 'http://www.w3.org/2000/svg';

function svg<K extends keyof SVGElementTagNameMap>(tag: K, attrs: Record<string, string | number>): SVGElementTagNameMap[K] {
  const node = document.createElementNS(SVGNS, tag);
  Object.entries(attrs).forEach(([name, value]) => node.setAttribute(name, String(value)));
  return node;
}

const crossPath = ([x, y]: [number, number], r = 1.6) =>
  `M${x - r},${y - r} L${x + r},${y + r} M${x - r},${y + r} L${x + r},${y - r}`;

/** A won foul or dribble that created a chance: a blue ring that reads around a player's dot. */
function createMarker([x, y]: [number, number], color: string): SVGGElement {
  const g = svg('g', {});
  g.appendChild(svg('circle', { cx: x, cy: y, r: 3.2, fill: 'none', stroke: color, 'stroke-width': 0.55 }));
  g.appendChild(svg('circle', { cx: x, cy: y, r: 0.9, fill: color }));
  return g;
}

interface MountedPlayer {
  g: SVGGElement;
  label: SVGTextElement;
  ring: SVGCircleElement | null;
  track: Track;
  star: boolean;
}

interface RendererNodes {
  layer: SVGGElement;
  ball: SVGCircleElement;
  trail: SVGPolylineElement;
}

interface RendererOptions {
  playerId: number;
  names: Record<string, string>;
  featuredName: string;
  markerId: string;
  onState: (state: PlaybackState) => void;
}

class ReplayRenderer {
  private nodes: RendererNodes;
  private opts: RendererOptions;
  private mode: PlaybackMode = 'still';
  private queue: QueueEntry[] = [];
  private qi = 0;
  private T = 0;
  private hold = 0;
  private hits = new Set<string>();
  private popped = new Set<string>();
  private badgeKey: string | null = null;
  private caption: string | null = null;
  private frame: number | null = null;
  private lastTs: number | null = null;
  private lines: ContextLine[] = [];
  private lineNodes: SVGLineElement[] = [];
  private markNodes: { mark: Mark; node: SVGElement }[] = [];
  private players: MountedPlayer[] = [];

  constructor(nodes: RendererNodes, opts: RendererOptions) {
    this.nodes = nodes;
    this.opts = opts;
  }

  play(queue: QueueEntry[], mode: 'single' | 'all') {
    if (!queue.length) return;
    this.mode = mode;
    this.queue = queue;
    this.qi = 0;
    this.hits = new Set();
    this.popped = new Set();
    this.startEntry();
    if (this.frame == null) {
      this.lastTs = null;
      this.frame = requestAnimationFrame(this.step);
    }
  }

  stop() {
    if (this.frame != null) cancelAnimationFrame(this.frame);
    this.frame = null;
    this.mode = 'still';
    this.queue = [];
    this.badgeKey = null;
    this.caption = null;
    this.nodes.layer.replaceChildren();
    this.nodes.ball.setAttribute('opacity', '0');
    this.nodes.trail.setAttribute('opacity', '0');
    this.emit();
  }

  destroy() {
    if (this.frame != null) cancelAnimationFrame(this.frame);
    this.frame = null;
  }

  private get entry(): QueueEntry {
    return this.queue[this.qi];
  }

  /** Moments that score in the current entry: the focus alone, or all of them in Play all. */
  private activePops() {
    const entry = this.entry;
    if (entry.caption != null && this.mode === 'single') return [];
    return this.mode === 'single' ? entry.item.pops.filter(p => p.key === entry.focus) : entry.item.pops;
  }

  private startEntry() {
    const entry = this.entry;
    this.T = entry.startT;
    this.hold = 0;
    this.caption = entry.caption;
    const cont = entry.caption != null ? (entry.focus ?? entry.item.conts[0]?.key ?? null) : null;
    if (cont) {
      this.badgeKey = cont;
      this.hits.add(cont);
    } else {
      const pops = this.activePops();
      this.badgeKey = (pops.find(p => p.at >= this.T - 0.01) ?? pops[0])?.key ?? null;
    }
    this.mount();
    this.draw();
    this.emit();
  }

  private mount() {
    const { layer, ball, trail } = this.nodes;
    layer.replaceChildren();
    // Opponent passing outside the possession window is not drawn.
    this.lines = visibleLines(this.entry);
    this.lineNodes = this.lines.map(line => {
      const node = svg('line', {
        x1: line.s[0], y1: line.s[1], x2: line.e[0], y2: line.e[1],
        stroke: line.side === 1 ? COLORS.us : COLORS.them,
        'stroke-width': line.shot ? 0.5 : 0.35,
        'stroke-linecap': 'round',
        opacity: 0,
      });
      if (!line.shot) node.setAttribute('stroke-dasharray', '1.2 1');
      layer.appendChild(node);
      return node;
    });
    // Lines draw under the players; point marks (lost ball, won foul) over
    // them, or the player's own dot would hide the moment.
    const pointMarks: SVGElement[] = [];
    this.markNodes = this.activePops().flatMap(pop => pop.marks).map(mark => {
      const color = MARK_COLORS[mark.kind];
      let node: SVGElement;
      if (mark.kind === 'lost') {
        node = svg('path', { d: crossPath(mark.s), stroke: color, 'stroke-width': 0.7, 'stroke-linecap': 'round' });
        pointMarks.push(node);
      } else if (mark.kind === 'create-dot') {
        node = createMarker(mark.s, color);
        pointMarks.push(node);
      } else {
        node = svg('line', {
          x1: mark.s[0], y1: mark.s[1], x2: mark.s[0], y2: mark.s[1],
          stroke: color, 'stroke-width': mark.kind === 'shot' ? 0.65 : 0.8, 'stroke-linecap': 'round',
        });
        layer.appendChild(node);
      }
      node.setAttribute('opacity', '0');
      return { mark, node };
    });
    this.players = visibleTracks(this.entry).map(track => {
      const star = track.pid === this.opts.playerId;
      const g = svg('g', { opacity: 0 });
      let ring: SVGCircleElement | null = null;
      if (star) {
        ring = svg('circle', { r: 2.6, fill: 'none', stroke: COLORS.accent, 'stroke-width': 0.6, opacity: 0 });
        g.appendChild(ring);
      }
      g.appendChild(svg('circle', {
        r: star ? 1.9 : 1.6,
        fill: track.side === 1 ? COLORS.us : COLORS.them,
        stroke: COLORS.bg,
        'stroke-width': 0.3,
      }));
      const label = svg('text', {
        y: star ? -3.6 : -2.8,
        'text-anchor': 'middle',
        'font-size': star ? 3.4 : 2.9,
        'font-weight': star ? 500 : 400,
        fill: COLORS.fg,
        'paint-order': 'stroke',
        stroke: COLORS.bg,
        'stroke-width': 0.8,
        opacity: 0,
      });
      // Only teammates are ever named; opponents stay unlabeled dots.
      label.textContent = star ? this.opts.featuredName : track.side === 1 ? this.opts.names[String(track.pid)] ?? '' : '';
      g.appendChild(label);
      layer.appendChild(g);
      return { g, label, ring, track, star };
    });
    pointMarks.forEach(node => layer.appendChild(node));
    ball.setAttribute('opacity', '1');
    trail.setAttribute('opacity', '1');
  }

  private draw() {
    const clip = this.entry.item.clip;
    const T = this.T;
    const [bx, by] = ballAt(clip.kf, T);
    this.nodes.ball.setAttribute('cx', bx.toFixed(2));
    this.nodes.ball.setAttribute('cy', by.toFixed(2));
    const trail: string[] = [];
    const trailFrom = trailStart(clip, T);
    for (let k = 10; k >= 0; k--) {
      const [x, y] = ballAt(clip.kf, Math.max(trailFrom, T - k * 0.1));
      trail.push(`${x.toFixed(2)},${y.toFixed(2)}`);
    }
    this.nodes.trail.setAttribute('points', trail.join(' '));

    // Faint context passes and shots, fading in and out around their time.
    this.lines.forEach((line, i) => {
      const age = T - line.t;
      const life = line.shot ? 3 : 2.2;
      let a = 0;
      if (age >= 0 && age <= life) a = Math.min(1, age / 0.15) * (1 - Math.max(0, (age - (life - 0.8)) / 0.8));
      this.lineNodes[i].setAttribute('opacity', (a * (line.shot ? 0.95 : 0.6)).toFixed(3));
    });

    // Scoring marks draw along with the ball, then hold for the rest of the clip.
    this.markNodes.forEach(({ mark, node }) => {
      const u = markProgress(mark, T);
      if (u < 0) { node.setAttribute('opacity', '0'); return; }
      node.setAttribute('opacity', '1');
      if (!mark.e) return;
      node.setAttribute('x2', (mark.s[0] + (mark.e[0] - mark.s[0]) * u).toFixed(2));
      node.setAttribute('y2', (mark.s[1] + (mark.e[1] - mark.s[1]) * u).toFixed(2));
      // Impact arrows ride the tip; skip the first instant when the line has no direction yet.
      if (mark.kind === 'impact') node.setAttribute('marker-end', u > 0.03 ? `url(#${this.opts.markerId})` : 'none');
    });

    const holder = holderAt(clip, T);
    const onBall = featuredOnBall(clip, this.opts.playerId, T);
    const states = this.players.map(p => {
      const pos = playerAt(p.track, T);
      const visible = !!pos && pos.a > 0.01;
      const labeled = visible && !!p.label.textContent && (p.star || (p.track.pid === holder && p.track.side === 1));
      return { p, pos, visible, labeled };
    });
    const spots = labelSpots(states.map(st => ({
      x: st.pos?.x ?? 0,
      y: st.pos?.y ?? 0,
      labeled: st.labeled,
      w: labelWidth(st.p.label.textContent ?? '', st.p.star),
      star: st.p.star,
    })));
    states.forEach(({ p, pos, visible, labeled }, i) => {
      if (!visible || !pos) { p.g.setAttribute('opacity', '0'); return; }
      p.g.setAttribute('transform', `translate(${pos.x.toFixed(2)},${pos.y.toFixed(2)})`);
      p.g.setAttribute('opacity', Math.max(0, Math.min(1, pos.a)).toFixed(3));
      const spot = spots[i];
      p.label.setAttribute('opacity', labeled && !spot.hidden ? '1' : '0');
      p.label.setAttribute('text-anchor', spot.anchor);
      p.label.setAttribute('x', String(spot.dx));
      p.label.setAttribute('y', String(labelOffset(p.star, spot.below)));
      if (p.ring) {
        p.ring.setAttribute('opacity', onBall ? '1' : '0');
        p.label.setAttribute('fill', onBall ? COLORS.accent : COLORS.fg);
      }
    });
  }

  /** Advances playback by dt; false once the queue has finished and playback stopped. */
  private tick(dt: number): boolean {
    if (this.hold > 0) {
      this.hold -= dt;
      if (this.hold <= 0) {
        this.qi += 1;
        if (this.qi < this.queue.length) this.startEntry();
        else {
          this.stop();
          return false;
        }
      }
      return true;
    }
    // Long replays play proportionally faster to fit the 10 s limit (entry.rate).
    this.T = advanceTime(this.entry.item.clip, this.T, dt * this.entry.rate);
    const step = scoreStep(this.activePops(), this.hits, this.badgeKey, this.T, this.mode === 'all');
    let changed = step.scored.length > 0;
    step.scored.forEach(key => {
      this.hits.add(key);
      if (this.mode === 'all') this.popped.add(key);
    });
    if (step.badgeKey !== this.badgeKey) {
      this.badgeKey = step.badgeKey;
      this.caption = null;
      changed = true;
    }
    const entry = this.entry;
    if (this.T >= entry.endT) {
      this.T = entry.endT;
      this.hold = this.mode === 'single' && this.qi === this.queue.length - 1 ? END_HOLD_SECONDS : BETWEEN_HOLD_SECONDS;
    }
    if (changed) this.emit();
    return true;
  }

  private step = (ts: number) => {
    if (this.mode === 'still') { this.frame = null; return; }
    const dt = this.lastTs == null ? 0 : Math.min(0.05, (ts - this.lastTs) / 1000);
    this.lastTs = ts;
    if (!this.tick(dt)) return;
    this.draw();
    this.frame = requestAnimationFrame(this.step);
  };

  private emit() {
    this.opts.onState({
      mode: this.mode,
      badgeKey: this.badgeKey,
      badgeHit: this.badgeKey != null && this.hits.has(this.badgeKey),
      caption: this.caption,
      poppedKeys: [...this.popped],
    });
  }
}

export interface MomentReplayHandle {
  playMoment: (key: string) => void;
}

export interface MomentBadgeLabel {
  minute: string;
  points: number;
}

interface MomentReplayPanelProps {
  /** loading: pitch placeholder; ready: pitch when there are clips; unavailable: total only. */
  status: 'loading' | 'ready' | 'unavailable';
  items: ReplayItem[];
  moments: ReplayMoment[];
  playerId: number;
  featuredName: string;
  names: Record<string, string>;
  directionLabel: string | null;
  labels: Map<string, MomentBadgeLabel>;
  total: number;
  onFocusChange: (key: string | null) => void;
}

const formatTotal = (n: number) => {
  const rounded = Math.round(n * 100) / 100;
  return rounded.toFixed(Number.isInteger(rounded * 10) ? 1 : 2);
};

const STRIPES = Array.from({ length: 12 }, (_, i) => i);

const MomentReplayPanel = forwardRef<MomentReplayHandle, MomentReplayPanelProps>(({
  status,
  items,
  moments,
  playerId,
  featuredName,
  names,
  directionLabel,
  labels,
  total,
  onFocusChange,
}, ref) => {
  const markerId = `moment-arrow-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const layerRef = useRef<SVGGElement>(null);
  const ballRef = useRef<SVGCircleElement>(null);
  const trailRef = useRef<SVGPolylineElement>(null);
  const pointsRef = useRef<HTMLSpanElement>(null);
  const rendererRef = useRef<ReplayRenderer | null>(null);
  const [playback, setPlayback] = useState<PlaybackState>(STILL);
  const hasPitch = status === 'loading' || (status === 'ready' && items.length > 0);
  const canPlay = status === 'ready' && items.length > 0;

  useEffect(() => {
    const layer = layerRef.current;
    const ball = ballRef.current;
    const trail = trailRef.current;
    if (!canPlay || !layer || !ball || !trail) return;
    const renderer = new ReplayRenderer({ layer, ball, trail }, { playerId, names, featuredName, markerId, onState: setPlayback });
    rendererRef.current = renderer;
    return () => {
      renderer.destroy();
      rendererRef.current = null;
      layer.replaceChildren();
      setPlayback(STILL);
    };
  }, [canPlay, items, playerId, names, featuredName, markerId]);

  useEffect(() => {
    onFocusChange(playback.mode === 'still' ? null : playback.badgeKey);
  }, [playback.mode, playback.badgeKey, onFocusChange]);

  // The points in the badge pop when the moment scores.
  useEffect(() => {
    if (!playback.badgeHit || !pointsRef.current) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    pointsRef.current.animate?.(
      [{ transform: 'scale(1)' }, { transform: 'scale(1.35)' }, { transform: 'scale(1)' }],
      { duration: 450, easing: 'ease-out' },
    );
  }, [playback.badgeKey, playback.badgeHit]);

  const playMoment = useCallback((key: string) => {
    rendererRef.current?.play(queueForMoment(items, key), 'single');
  }, [items]);

  useImperativeHandle(ref, () => ({ playMoment }), [playMoment]);

  const toggleAll = () => {
    const renderer = rendererRef.current;
    if (!renderer) return;
    if (playback.mode === 'still') renderer.play(queueForAll(items), 'all');
    else renderer.stop();
  };

  // Still map: everything the player scored from, each mark replays its moment.
  const stillMarks = useMemo(
    () => items.flatMap(item => item.pops.flatMap(pop => pop.marks.map(mark => ({ key: pop.key, mark })))),
    [items],
  );

  const pointsByKey = useMemo(() => new Map(moments.map(m => [m.key, m.points])), [moments]);
  const runningTotal = playback.poppedKeys.reduce((sum, key) => sum + (pointsByKey.get(key) ?? 0), 0);
  // Play all counts up to what it plays; rows without a replay are only in the still total.
  const playableTotal = useMemo(() => moments.reduce((sum, m) => sum + m.points, 0), [moments]);
  const badge = playback.badgeKey ? labels.get(playback.badgeKey) : undefined;
  const still = playback.mode === 'still';

  return (
    <div className="border-b border-gray-700/50 bg-surface-card">
      {hasPitch && (
        <div className="relative">
          <svg viewBox="-3 -3 126 86" className="block h-auto max-h-[40dvh] w-full" style={{ background: COLORS.pitchA }} role="img" aria-label="Pitch replay of the player's scoring moments">
            <defs>
              <marker id={markerId} viewBox="0 0 10 10" refX="7" refY="5" markerWidth="4.2" markerHeight="4.2" orient="auto">
                <path d="M0,0 L10,5 L0,10 z" fill={MARK_COLORS.impact} />
              </marker>
            </defs>
            <g>
              {STRIPES.map(i => (
                <rect key={i} x={i * 10} y={0} width={10} height={80} fill={i % 2 ? COLORS.pitchB : COLORS.pitchA} />
              ))}
            </g>
            <g fill="none" stroke={COLORS.pitchLine} strokeWidth={0.35}>
              <rect x={0} y={0} width={120} height={80} />
              <line x1={60} y1={0} x2={60} y2={80} />
              <circle cx={60} cy={40} r={10} />
              <rect x={0} y={18} width={18} height={44} />
              <rect x={0} y={30} width={6} height={20} />
              <path d="M18,32 A10,10 0 0,1 18,48" />
              <rect x={102} y={18} width={18} height={44} />
              <rect x={114} y={30} width={6} height={20} />
              <path d="M102,32 A10,10 0 0,0 102,48" />
              <rect x={-2} y={36} width={2} height={8} />
              <rect x={120} y={36} width={2} height={8} />
            </g>
            <g fill={COLORS.pitchLine}>
              <circle cx={60} cy={40} r={0.45} />
              <circle cx={12} cy={40} r={0.45} />
              <circle cx={108} cy={40} r={0.45} />
            </g>
            {directionLabel && (
              // Inside the viewBox (it ends at y=83) with its descenders, under the bottom touchline.
              <text x={119} y={82.3} textAnchor="end" fontSize={2.4} fill={COLORS.muted}>{directionLabel}</text>
            )}
            <g opacity={still ? 1 : 0} style={{ pointerEvents: still ? 'auto' : 'none' }}>
              {stillMarks.map(({ key, mark }, i) => (
                <StillMark key={`${key}-${i}`} mark={mark} markerId={markerId} onPlay={() => playMoment(key)} />
              ))}
            </g>
            <g ref={layerRef} />
            <polyline ref={trailRef} fill="none" stroke={COLORS.fg} strokeOpacity={0.28} strokeWidth={0.7} strokeLinecap="round" strokeLinejoin="round" points="" opacity={0} />
            <circle ref={ballRef} r={1.05} fill="#ffffff" stroke={COLORS.bg} strokeWidth={0.35} cx={60} cy={40} opacity={0} />
          </svg>
          {status === 'loading' && (
            <div className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
              <div className="h-7 w-7 animate-spin rounded-full border-b-2 border-accent-yellow" />
            </div>
          )}
          {canPlay && (
            still ? (
              <div className="pointer-events-none absolute left-2 top-2 rounded-lg border border-gray-600/50 bg-[#080e1a]/85 px-2.5 py-1.5 text-xs text-gray-300">
                Tap a moment to replay it
              </div>
            ) : badge ? (
              <div className="pointer-events-none absolute left-2 top-2 flex max-w-[calc(100%-1rem)] flex-col gap-0.5 rounded-lg border border-gray-600/50 bg-[#080e1a]/85 px-2.5 py-1.5" aria-live="polite">
                <span className="flex items-baseline gap-2">
                  <span className="font-heading text-lg font-bold leading-none text-white">{badge.minute}</span>
                  <span
                    ref={pointsRef}
                    className={`inline-block font-heading text-base font-bold leading-none tabular-nums ${playback.badgeHit ? (badge.points < 0 ? 'text-red-400' : 'text-green-400') : 'text-gray-500'}`}
                  >
                    {signedPoints(badge.points)}
                  </span>
                </span>
                {playback.caption && <span className="text-[11.5px] text-accent-yellow">{playback.caption}</span>}
              </div>
            ) : null
          )}
        </div>
      )}
      <div className="flex items-center justify-between gap-3 px-3 py-2">
        {canPlay ? (
          <button
            type="button"
            onClick={toggleAll}
            className="inline-flex min-h-10 min-w-[7rem] items-center justify-center gap-1.5 rounded-lg bg-accent-yellow px-3.5 text-sm font-bold text-primary-dark transition-colors hover:bg-accent-yellow/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
          >
            {still ? <Play size={14} fill="currentColor" /> : <Square size={13} fill="currentColor" />}
            {still ? 'Play all' : 'Stop'}
          </button>
        ) : <span />}
        <div className="text-right leading-tight">
          <span className="block text-[10.5px] uppercase tracking-wider text-gray-400">
            {playback.mode === 'all' ? 'Running total' : 'Total fantasy points'}
          </span>
          <span className="font-heading text-2xl font-bold tabular-nums text-accent-yellow">
            {formatTotal(playback.mode === 'all' ? runningTotal : total)}
          </span>
          {playback.mode === 'all' && <span className="text-xs text-gray-400"> of {formatTotal(playableTotal)}</span>}
        </div>
      </div>
    </div>
  );
});

MomentReplayPanel.displayName = 'MomentReplayPanel';

const StillMark: React.FC<{ mark: Mark; markerId: string; onPlay: () => void }> = ({ mark, markerId, onPlay }) => {
  const color = MARK_COLORS[mark.kind];
  const [x, y] = mark.s;
  if (mark.kind === 'lost' || mark.kind === 'create-dot') {
    return (
      <g>
        {mark.kind === 'lost'
          ? <path d={crossPath(mark.s)} stroke={color} strokeWidth={0.7} strokeLinecap="round" />
          : (
            <>
              <circle cx={x} cy={y} r={3.2} fill="none" stroke={color} strokeWidth={0.55} />
              <circle cx={x} cy={y} r={0.9} fill={color} />
            </>
          )}
        <circle cx={x} cy={y} r={3} fill="transparent" className="cursor-pointer" onClick={onPlay} aria-hidden="true" />
      </g>
    );
  }
  const [ex, ey] = mark.e ?? mark.s;
  return (
    <g>
      <line
        x1={x} y1={y} x2={ex} y2={ey}
        stroke={color}
        strokeWidth={mark.kind === 'shot' ? 0.65 : 0.8}
        strokeLinecap="round"
        opacity={0.95}
        markerEnd={mark.kind === 'impact' ? `url(#${markerId})` : undefined}
      />
      <circle cx={x} cy={y} r={0.9} fill={color} />
      <line x1={x} y1={y} x2={ex} y2={ey} stroke="transparent" strokeWidth={4} strokeLinecap="round" className="cursor-pointer" onClick={onPlay} aria-hidden="true" />
    </g>
  );
};

export default MomentReplayPanel;
